import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Loader2, TriangleAlert } from 'lucide-react';
import { T } from '../i18n/copy';
import { receivedPath } from '../i18n/paths';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const URGENCY_VALUES = ['exploring', 'soon', 'active_priority', 'urgent'];
const SIZE_VALUES = ['1-9', '10-49', '50-199', '200-499', '500+'];

const inputCls =
  'w-full border border-midnight/15 bg-[#FAF9F3] px-4 py-3 text-base text-midnight transition-colors duration-200 placeholder:text-steel-dark/60 focus:border-vermilion focus:outline-none focus:ring-1 focus:ring-vermilion';

export default function ClinicForm({ lang }) {
  const navigate = useNavigate();
  const t = T.form;
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    fullName: '', email: '', role: '', company: '', website: '', industry: '', size: '',
    improve: '', friction: '', workflow: '', impact: '', better: '', urgency: '', more: '',
    consent: false, hp: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [apiBanner, setApiBanner] = useState('');
  const F = t.fields;

  const set = (k, v) => {
    setData((d) => ({ ...d, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const MIN_LEN_KEYS = ['improve', 'friction', 'workflow', 'impact', 'better'];

  const validateStep = (s) => {
    const e = {};
    const req = (k) => { if (!String(data[k]).trim()) e[k] = 'required'; };
    const minLen = (k) => { if (!e[k] && String(data[k]).trim().length < 10) e[k] = 'minLength'; };
    if (s === 0) {
      req('fullName'); req('role');
      if (!data.email.trim()) e.email = 'required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) e.email = 'invalidEmail';
    } else if (s === 1) {
      req('company');
    } else {
      MIN_LEN_KEYS.forEach((k) => { req(k); minLen(k); });
      if (!data.urgency) e.urgency = 'required';
      if (!data.consent) e.consent = 'required';
    }
    return e;
  };

  // Errors are stored as keys and resolved against the CURRENT locale at render,
  // so switching EN/ID re-renders every message in the active language.
  const errText = (k) => ({ required: t.required, minLength: t.minLength, invalidEmail: t.invalidEmail }[errors[k]]?.[lang]);

  const focusFirstError = (e) => {
    const order = ['fullName', 'email', 'role', 'company', 'size', 'improve', 'friction', 'workflow', 'impact', 'better', 'urgency', 'more', 'consent'];
    const first = order.find((k) => e[k]);
    if (!first) return;
    const el = first === 'consent'
      ? document.getElementById('f-consent')
      : first === 'urgency'
        ? document.querySelector('[data-testid=clinic-form-urgency]')
        : document.getElementById(`f-${first}`);
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };

  const next = () => {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length) { focusFirstError(e); return; }
    setStep((v) => v + 1);
  };

  const FIELD_TO_KEY = {
    full_name: 'fullName', work_email: 'email', role_title: 'role', company_name: 'company',
    company_website: 'website', industry: 'industry', company_size: 'size',
    trying_to_improve: 'improve', current_friction: 'friction', workflow_today: 'workflow',
    business_impact: 'impact', better_outcome: 'better', anything_else: 'more',
    urgency: 'urgency', consent: 'consent', website_url_confirm: 'hp',
  };

  const submit = async () => {
    const e = validateStep(2);
    setErrors(e);
    if (Object.keys(e).length) { focusFirstError(e); return; }
    setStatus('submitting');
    let apiErrors = {};
    let bannerMsg = 'errorBanner';
    try {
      const res = await fetch(`${API}/business-clinic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: data.fullName.trim(),
          work_email: data.email.trim(),
          role_title: data.role.trim(),
          company_name: data.company.trim(),
          company_website: data.website.trim() || null,
          industry: data.industry.trim() || null,
          company_size: data.size || null,
          trying_to_improve: data.improve.trim(),
          current_friction: data.friction.trim(),
          workflow_today: data.workflow.trim(),
          business_impact: data.impact.trim(),
          better_outcome: data.better.trim(),
          anything_else: data.more.trim() || null,
          urgency: data.urgency,
          consent: data.consent,
          language: lang,
          website_url_confirm: data.hp || '',
        }),
      });
      if (res.ok) {
        setStatus('idle');
        navigate(receivedPath(lang));
        return;
      }
      const body = await res.json().catch(() => null);
      if (res.status === 429) {
        bannerMsg = 'rateLimited';
      } else if (res.status === 422 && Array.isArray(body?.detail)) {
        body.detail.forEach((d) => {
          const key = FIELD_TO_KEY[d?.loc?.[1]];
          if (!key || apiErrors[key]) return;
          apiErrors[key] = d?.loc?.[1] === 'work_email'
            ? 'invalidEmail'
            : d?.type === 'string_too_short'
              ? 'minLength'
              : 'required';
        });
      }
    } catch {
      bannerMsg = 'networkError';
    }
    setErrors(apiErrors);
    setApiBanner(bannerMsg);
    setStatus('error');
    focusFirstError(apiErrors);
  };

  const fieldWrap = (k, label, required, children) => (
    <div key={k} className={k === 'fullName' || k === 'company' || k === 'size' || k === 'improve' || k === 'friction' || k === 'workflow' || k === 'impact' || k === 'better' || k === 'urgency' || k === 'more' ? '' : ''}>
      <label htmlFor={`f-${k}`} className="mb-1.5 block text-[12px] font-medium tracking-wide text-midnight/80">
        {label}{required && <span className="text-vermilion"> *</span>}
      </label>
      {children}
      {errors[k] && (
        <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-vermilion" data-testid={`form-error-${k}`}>
          <TriangleAlert className="h-3 w-3" strokeWidth={2} />
          {errText(k)}
        </p>
      )}
    </div>
  );

  return (
    <div className="mt-8" data-testid="clinic-form">
      <ol className="flex border border-midnight/15 bg-bone" data-testid="clinic-form-steps">
        {t.steps[lang].map((s, i) => (
          <li key={s} className="flex flex-1 flex-col items-start gap-2 border-r border-midnight/10 px-3 py-3 last:border-r-0 sm:flex-row sm:items-center sm:gap-3 sm:px-4 md:px-5">
            <span className={`flex h-7 w-7 shrink-0 items-center justify-center text-[11px] font-medium ${
              i < step ? 'bg-vermilion text-bone' : i === step ? 'bg-midnight text-bone' : 'border border-midnight/25 text-midnight/50'
            }`}>
              {i < step ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : `0${i + 1}`}
            </span>
            <span className={`text-[11.5px] font-medium leading-tight sm:text-[13px] ${i === step ? 'text-midnight' : 'text-midnight/50'}`}>{s}</span>
          </li>
        ))}
      </ol>

      <div className="border border-t-0 border-midnight/15 bg-bone p-6 md:p-10" data-testid={`clinic-form-panel-${step}`}>
        {status === 'error' && (
          <div role="alert" data-testid="form-error-banner" className="mb-6 flex items-center gap-2 border border-vermilion bg-vermilion/5 px-4 py-3 text-[13px] text-vermilion">
            <TriangleAlert className="h-4 w-4 shrink-0" strokeWidth={2} />
            {(t[apiBanner] || t.errorBanner)[lang]}
          </div>
        )}

        {step === 0 && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="md:col-span-2">{fieldWrap('fullName', F.fullName.label[lang], F.fullName.required, (
              <input id="f-fullName" data-testid="clinic-form-input-name" type="text" autoComplete="name" maxLength={120} placeholder={F.fullName.placeholder[lang]} value={data.fullName} onChange={(e) => set('fullName', e.target.value)} className={`${inputCls} ${errors.fullName ? 'border-vermilion' : ''}`} />
            ))}</div>
            {fieldWrap('email', F.email.label[lang], F.email.required, (
              <input id="f-email" data-testid="clinic-form-input-email" type="email" autoComplete="email" maxLength={200} placeholder={F.email.placeholder[lang]} value={data.email} onChange={(e) => set('email', e.target.value)} className={`${inputCls} ${errors.email ? 'border-vermilion' : ''}`} />
            ))}
            {fieldWrap('role', F.role.label[lang], F.role.required, (
              <input id="f-role" data-testid="clinic-form-input-role" type="text" autoComplete="organization-title" maxLength={120} placeholder={F.role.placeholder[lang]} value={data.role} onChange={(e) => set('role', e.target.value)} className={`${inputCls} ${errors.role ? 'border-vermilion' : ''}`} />
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="md:col-span-2">{fieldWrap('company', F.company.label[lang], F.company.required, (
              <input id="f-company" data-testid="clinic-form-input-company" type="text" autoComplete="organization" maxLength={160} placeholder={F.company.placeholder[lang]} value={data.company} onChange={(e) => set('company', e.target.value)} className={`${inputCls} ${errors.company ? 'border-vermilion' : ''}`} />
            ))}</div>
            {fieldWrap('website', F.website.label[lang], false, (
              <input id="f-website" data-testid="clinic-form-input-website" type="url" autoComplete="url" maxLength={200} placeholder={F.website.placeholder[lang]} value={data.website} onChange={(e) => set('website', e.target.value)} className={inputCls} />
            ))}
            {fieldWrap('industry', F.industry.label[lang], false, (
              <input id="f-industry" data-testid="clinic-form-input-industry" type="text" maxLength={120} placeholder={F.industry.placeholder[lang]} value={data.industry} onChange={(e) => set('industry', e.target.value)} className={inputCls} />
            ))}
            {fieldWrap('size', F.size.label[lang], false, (
              <select id="f-size" data-testid="clinic-form-input-size" value={data.size} onChange={(e) => set('size', e.target.value)} className={`${inputCls} ${data.size ? '' : 'text-steel-dark/70'}`}>
                <option value="">{t.sizePlaceholder[lang]}</option>
                {SIZE_VALUES.map((v, i) => (
                  <option key={v} value={v}>{F.size.options[lang][i]}</option>
                ))}
              </select>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 gap-6">
            <div className="border border-midnight/15 bg-[#FAF9F3] p-5" data-testid="clinic-form-security-notice">
              <p className="eyebrow text-midnight/70">{t.securityTitle[lang]}</p>
              <p className="mt-2 text-[12.5px] leading-relaxed text-midnight/60">{t.security[lang]}</p>
            </div>
            {['improve', 'friction', 'workflow', 'impact', 'better'].map((k) =>
              fieldWrap(k, F[k].label[lang], F[k].required, (
                <textarea
                  id={`f-${k}`}
                  data-testid={`clinic-form-input-${k}`}
                  rows={k === 'workflow' ? 4 : 3}
                  maxLength={3000}
                  placeholder={F[k].placeholder[lang]}
                  value={data[k]}
                  onChange={(e) => set(k, e.target.value)}
                  className={`${inputCls} resize-y ${errors[k] ? 'border-vermilion' : ''}`}
                />
              )),
            )}

            <div>
              <span className="mb-2 block text-[12px] font-medium tracking-wide text-midnight/80">
                {F.urgency.label[lang]}<span className="text-vermilion"> *</span>
              </span>
              <div role="radiogroup" aria-label={F.urgency.label[lang]} className="grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="clinic-form-urgency">
                {F.urgency.options[lang].map((opt, i) => (
                  <label key={opt} className="cursor-pointer">
                    <input
                      type="radio"
                      name="urgency"
                      value={URGENCY_VALUES[i]}
                      checked={data.urgency === URGENCY_VALUES[i]}
                      onChange={() => set('urgency', URGENCY_VALUES[i])}
                      className="peer sr-only"
                      data-testid={`clinic-form-urgency-${URGENCY_VALUES[i]}`}
                    />
                    <span className="flex items-center justify-center border border-midnight/15 bg-[#FAF9F3] px-3 py-3 text-center text-[12.5px] text-midnight/75 transition-colors duration-200 peer-checked:border-midnight peer-checked:bg-midnight peer-checked:text-bone peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-vermilion">
                      {opt}
                    </span>
                  </label>
                ))}
              </div>
              {errors.urgency && (
                <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-vermilion" data-testid="form-error-urgency">
                  <TriangleAlert className="h-3 w-3" strokeWidth={2} />{errText('urgency')}
                </p>
              )}
            </div>

            {fieldWrap('more', F.more.label[lang], false, (
              <textarea id="f-more" data-testid="clinic-form-input-more" rows={3} maxLength={3000} placeholder={F.more.placeholder[lang]} value={data.more} onChange={(e) => set('more', e.target.value)} className={`${inputCls} resize-y`} />
            ))}

            <div>
              <label className="flex cursor-pointer items-start gap-3" data-testid="clinic-form-consent">
                <input
                  type="checkbox"
                  checked={data.consent}
                  onChange={(e) => set('consent', e.target.checked)}
                  className="peer sr-only"
                  id="f-consent"
                  data-testid="clinic-form-consent-checkbox"
                />
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-midnight/30 bg-[#FAF9F3] transition-colors duration-200 peer-checked:border-vermilion peer-checked:bg-vermilion peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-vermilion">
                  {data.consent && <Check className="h-3.5 w-3.5 text-bone" strokeWidth={2.5} />}
                </span>
                <span className="text-base leading-relaxed text-midnight/75">{t.consent[lang]}<span className="text-vermilion"> *</span></span>
              </label>
              {errors.consent && (
                <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-vermilion" data-testid="form-error-consent">
                  <TriangleAlert className="h-3 w-3" strokeWidth={2} />{errText('consent')}
                </p>
              )}
            </div>

            <div className="border border-midnight/15 bg-[#FAF9F3] p-5" data-testid="clinic-form-nonguarantee">
              <p className="text-[12.5px] leading-relaxed text-midnight/60">{t.nonGuarantee[lang]}</p>
            </div>

            {/* Honeypot — humans never see or fill this field */}
            <input
              type="text"
              name="website_url_confirm"
              value={data.hp}
              onChange={(e) => set('hp', e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-4">
          {step > 0 ? (
            <button
              type="button"
              data-testid="clinic-form-back-button"
              onClick={() => setStep((v) => v - 1)}
              className="border border-midnight/25 px-6 py-3.5 text-[13px] font-medium text-midnight transition-colors duration-200 hover:border-midnight hover:bg-midnight hover:text-bone"
            >
              {t.back[lang]}
            </button>
          ) : <span />}
          {step < 2 ? (
            <button
              type="button"
              data-testid="clinic-form-next-button"
              onClick={next}
              className="group inline-flex items-center gap-3 bg-vermilion px-8 py-3.5 text-[13px] font-medium tracking-[0.06em] text-bone transition-colors duration-300 hover:bg-[#A8402C]"
            >
              {t.next[lang]}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} />
            </button>
          ) : (
            <button
              type="button"
              data-testid="clinic-form-submit-button"
              onClick={submit}
              disabled={status === 'submitting'}
              className="inline-flex items-center gap-3 bg-vermilion px-8 py-3.5 text-[13px] font-medium tracking-[0.06em] text-bone transition-colors duration-300 hover:bg-[#A8402C] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {status === 'submitting' ? (
                <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />{t.submitting[lang]}</>
              ) : (
                <>{t.submit[lang]}<ArrowRight className="h-4 w-4" strokeWidth={2} /></>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
